import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for SSE (Server-Sent Events) streaming using EventSource
 * Proper SSE implementation
 * Handles event types: init, chunk, complete, error
 * @returns {Object} - { streamData, currentChunk, isStreaming, error, startStream, stopStream }
 */
export const useSSEStream = () => {
    const [streamData, setStreamData] = useState(null);
    const [currentChunk, setCurrentChunk] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState(null);
    const eventSourceRef = useRef(null);

    /**
     * Start SSE stream using EventSource (proper SSE implementation)
     * @param {string} url - The SSE endpoint URL with query parameters (including token)
     * @param {Object} options - { onInit, onChunk, onComplete, onError }
     */
    const startStream = useCallback((url, { onInit, onChunk, onComplete, onError } = {}) => {
        console.log('🎬 [startStream] Called with URL:', url.replace(/token=[^&]+/, 'token=***'));

        // Close any existing connection
        if (eventSourceRef.current) {
            console.log('🔄 [startStream] Closing existing connection');
            eventSourceRef.current.close();
        }

        setIsStreaming(true);
        setError(null);
        setStreamData(null);
        setCurrentChunk('');

        try {
            console.log('📡 [startStream] Creating EventSource...');

            // Create EventSource connection
            const eventSource = new EventSource(url);
            eventSourceRef.current = eventSource;

            console.log('✅ [startStream] EventSource created');

            // Handle incoming messages
            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    console.log('[SSE] Event received:', data.type);

                    switch (data.type) {
                        case 'init':
                            console.log('[SSE] Stream initialized:', data);
                            if (onInit) {
                                onInit(data);
                            }
                            break;

                        case 'chunk':
                            console.log('[SSE] Chunk received:', data);
                            // Extract text from either data.text OR data.chat[0].text_response
                            // NOTE: Chunks contain FULL accumulated text, not incremental text
                            const chunkText = data.chat?.[0]?.text_response || data.text || '';
                            // console.log('[SSE] Chunk text extracted:', {
                            //     hasChat: !!data.chat,
                            //     textFromChat: data.chat?.[0]?.text_response?.substring(0, 50),
                            //     textFromDirect: data.text?.substring(0, 50),
                            //     extractedText: chunkText.substring(0, 50),
                            //     length: chunkText.length
                            // });
                            // Replace (not append) because chunks contain full accumulated text
                            setCurrentChunk(chunkText);
                            if (onChunk) {
                                onChunk(data);
                            }
                            break;

                        case 'complete':
                            console.log('✅ [SSE] Stream completed');
                            console.log('📦 [SSE] Complete event data:', data);

                            setStreamData(data);
                            setCurrentChunk('');
                            setIsStreaming(false);
                            eventSource.close();

                            if (onComplete) {
                                console.log('📤 [SSE] Calling onComplete callback with:', data);
                                onComplete(data);
                            } else {
                                console.warn('⚠️ [SSE] No onComplete callback provided!');
                            }
                            break;

                        case 'error':
                            // Normalize error message across possible payload shapes
                            const msg = data.message || data.error || data.detail || data.text || data?.chat?.[0]?.text_response || 'Unknown streaming error';
                            console.error('[SSE] Stream error:', msg, '| payload:', data);
                            const errorObj = new Error(msg);
                            // Attach raw payload for upstream handlers/debugging
                            errorObj.payload = data;
                            setError(errorObj);
                            setIsStreaming(false);
                            setCurrentChunk('');
                            eventSource.close();

                            if (onError) {
                                onError(data);
                            }
                            break;

                        default:
                            console.warn('[SSE] Unknown event type:', data.type);
                    }

                } catch (err) {
                    console.error('[SSE] Error parsing event data:', err);
                    console.error('[SSE] Raw data:', event?.data);
                    // If server sent plain-text error, surface it
                    const fallbackMsg = typeof event?.data === 'string' && event.data.trim().length > 0
                        ? event.data
                        : 'Invalid SSE message';
                    const normalized = new Error(fallbackMsg);
                    setError(normalized);
                    setIsStreaming(false);
                    eventSource.close();

                    if (onError) {
                        onError(normalized);
                    }
                }
            };

            // Handle connection open
            eventSource.onopen = () => {
                console.log('✅ [SSE] Connection opened');
            };

            // Handle connection errors
            eventSource.onerror = (err) => {
                // Some browsers emit a generic Event without message; create a useful error
                const normalized = err instanceof Error ? err : new Error('SSE connection error');
                console.error('[SSE] Connection error:', normalized);
                setIsStreaming(false);
                setError(normalized);
                setCurrentChunk('');
                eventSource.close();

                if (onError) {
                    onError(normalized);
                }
            };

        } catch (err) {
            console.error('[SSE] Error starting stream:', err);
            setIsStreaming(false);
            setError(err);
            if (onError) {
                onError(err);
            }
        }
    }, []);

    /**
     * Stop the SSE stream
     */
    const stopStream = useCallback(() => {
        if (eventSourceRef.current) {
            console.log('🛑 [stopStream] Closing EventSource');
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        setIsStreaming(false);
        setCurrentChunk('');
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (eventSourceRef.current) {
                console.log('🧹 [useSSEStream] Cleanup: Closing EventSource on unmount');
                eventSourceRef.current.close();
            }
        };
    }, []);

    return {
        streamData,
        currentChunk,
        isStreaming,
        error,
        startStream,
        stopStream
    };
};

export default useSSEStream;
